import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { BaSeButton } from "@base/react";
import "./index.scss";
import {eventTag} from "../../services/utils";
import ApiGeneral from "../../services/general";

const TOOLTIP_WIDTH = 320;
const TOOLTIP_OFFSET = 16;

const getPlacementStyle = (targetRect, placement, tooltipRef) => {
  if (!targetRect) return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };

  const tooltipHeight = tooltipRef.current?.offsetHeight || 160;

  switch (placement) {
    case "top":
      return {
        top: targetRect.top - tooltipHeight - TOOLTIP_OFFSET,
        left: targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2,
      };
    case "bottom":
      return {
        top: targetRect.bottom + TOOLTIP_OFFSET,
        left: targetRect.left + targetRect.width / 2 - TOOLTIP_WIDTH / 2,
      };
    case "left":
      return {
        top: targetRect.top + targetRect.height / 2 - tooltipHeight / 2,
        left: targetRect.left - TOOLTIP_WIDTH - TOOLTIP_OFFSET,
      };
    case "right":
      return {
        top: targetRect.top + targetRect.height / 2 - tooltipHeight / 2,
        left: targetRect.right + TOOLTIP_OFFSET,
      };
    case "center":
    default:
      return { top: targetRect.top, left: targetRect.left };
  }
};

const getArrowClass = (placement) => {
  switch (placement) {
    case "top": return "onboarding-tooltip__arrow--top";
    case "bottom": return "onboarding-tooltip__arrow--bottom";
    case "left": return "onboarding-tooltip__arrow--left";
    case "right": return "onboarding-tooltip__arrow--right";
    default: return null;
  }
};

const OnboardingTooltip = ({ steps = [], run = false, onFinish, eventGA }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [locationRect, setLocationRect] = useState(null);
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef(null);

  const currentStep = steps[stepIndex];
  const total = steps.length;
  const placement = currentStep?.placement || "center";
  const hasLocation = !!currentStep?.location;
  const hasTarget = !!currentStep?.target;
  const isCenter = !hasLocation && !hasTarget;

  useEffect(() => {
    if (run) {
      setStepIndex(0);
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [run]);

  useEffect(() => {
    if (!visible) return;

    let attempts = 0;
    const maxAttempts = 20;

    const tryMeasure = () => {
      const target = currentStep?.target ? document.querySelector(currentStep.target) : null;
      const location = currentStep?.location ? document.querySelector(currentStep.location) : null;

      // Se espera um target/location mas ainda não está no DOM, tenta de novo
      if ((currentStep?.target && !target) || (currentStep?.location && !location)) {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(tryMeasure, 100);
          return;
        }
      }

      setTargetRect(target ? target.getBoundingClientRect() : null);
      setLocationRect(location ? location.getBoundingClientRect() : null);
    };

    tryMeasure();
  }, [visible, stepIndex, currentStep]);

  
  useEffect(() => {
    document.body.style.overflow = visible ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [visible]);

  const handleNext = () => {
    currentStep?.onNext?.();
    eventTag(eventGA?.name, "tutorial", eventGA?.action, `${stepIndex === total - 1 ? "Concluir" : "Próximo"}`)
    if (stepIndex < total - 1) {
      setStepIndex((s) => s + 1);
    } else {
      setVisible(false);
      ApiGeneral().sendDigitalMoments('access_manual')
      onFinish?.();
    }
  };

  const handlePrev = () => {
    currentStep?.onPrev?.();
    eventTag(eventGA?.name, "tutorial", eventGA?.action, "Anterior")
    if (stepIndex > 0) setStepIndex((s) => s - 1);
  };

  const handleSkip = () => {
    setVisible(false);
    eventTag(eventGA?.name, "tutorial", eventGA?.action, "Fechar")
    ApiGeneral().sendDigitalMoments('access_manual')
    onFinish?.();
  };

  if (!visible || !currentStep) return null;

  const spotlightPadding = 8;
  const spotlight = targetRect
    ? {
        top: targetRect.top - spotlightPadding,
        left: targetRect.left - spotlightPadding,
        width: targetRect.width + spotlightPadding * 2,
        height: targetRect.height + spotlightPadding * 2,
      }
    : null;

  const anchorRect = locationRect || targetRect;
  const tooltipPositionStyle = isCenter
    ? { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }
    : { position: "fixed", ...getPlacementStyle(anchorRect, placement, tooltipRef) };

  const arrowClass = hasTarget ? getArrowClass(placement) : null;

  return createPortal(
    <div className="onboarding-tooltip__root">
      <svg className="onboarding-tooltip__svg" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <mask id="onboarding-mask">
            <rect width="100%" height="100%" fill="white" />
            {spotlight && (
              <rect
                x={spotlight.left}
                y={spotlight.top}
                width={spotlight.width}
                height={spotlight.height}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.48)" mask="url(#onboarding-mask)" />
      </svg>

      <div ref={tooltipRef} className="onboarding-tooltip__box" style={tooltipPositionStyle}>
        {arrowClass && <div className={`onboarding-tooltip__arrow ${arrowClass}`} />}

        <div className="onboarding-tooltip__header">
          <span className="onboarding-tooltip__title">{currentStep.title}</span>
          <button className="onboarding-tooltip__close" onClick={handleSkip} aria-label="Fechar onboarding">
            x
          </button>
        </div>

        <p className="onboarding-tooltip__content">{currentStep.content}</p>

        <div className="onboarding-tooltip__footer">
          <span className="onboarding-tooltip__step-count">{stepIndex + 1} de {total}</span>
          <div className="onboarding-tooltip__footer-btns">
            <BaSeButton
              type="secondary"
              value="Anterior"
              color="#2E5DE6"
              isBold
              width={75}
              isDisabled={stepIndex === 0}
              onClick={handlePrev}
            />
            <BaSeButton
              type="secondary"
              value={stepIndex === total - 1 ? "Concluir" : "Próximo"}
              color="#2E5DE6"
              width={75}
              onClick={handleNext}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OnboardingTooltip;
